import { Stethoscope, Settings as SettingsIcon, Layers, History, BookOpen, Plus, Activity, Wallet, FileText, Ruler, Compass, Camera, Trophy, Youtube, Droplet, Plane, Newspaper, Cpu, Languages, Gamepad2, MessagesSquare, Brain, UserCog, TrendingUp, Inbox, VenetianMask, CheckCircle2, ScanLine, Moon, Library, Target, Users2, Radar, Contact, Gift } from 'lucide-react'

// Off-nav routes that get a transient extra slot on the bottom tab
// bar while the user is on them. The slot disappears the moment the
// user picks a main tab (because pathname changes and this map no
// longer resolves to anything).
//
// `match` is exact unless `prefixOnly: true` (used for dynamic
// segments like `/workout/sessions/:id` or `/workout/exercises/:id`).
// `labelKey` is a fully-qualified i18n key — the BottomTabBar will
// use it directly instead of the default `phoenixTracker.tabs.${key}`
// lookup it uses for the fixed tabs.
export const EPHEMERAL_TABS = [
  { match: '/health',    key: 'health',     icon: Stethoscope,  labelKey: 'healthProfile.title' },
  { match: '/finance',   key: 'finance',    icon: Wallet,       labelKey: 'finance.title' },
  { match: '/sports',    key: 'sports',     icon: Trophy,       labelKey: 'sports.title', prefixOnly: true },
  { match: '/youtube',   key: 'youtube',    icon: Youtube,      labelKey: 'news.youtube.title' },
  // News moved off the primary bar (social took its slot). Surface
  // it as an ephemeral chip so the user has a visual anchor when on
  // /news. prefixOnly catches /news/:id and /news/health too.
  { match: '/news',      key: 'news',       icon: Newspaper,    labelKey: 'news.title', prefixOnly: true },
  { match: '/tech',      key: 'tech',       icon: Cpu,          labelKey: 'tech.title', prefixOnly: true },
  { match: '/reports',   key: 'reports',    icon: FileText,     labelKey: 'reports.title' },
  // Owner-only — the JourneyPage redirects non-owners, so the slot
  // can't appear for them in practice (they can't reach the URL).
  { match: '/journey',   key: 'journey',    icon: Compass,      labelKey: 'journey.title' },
  { match: '/immigration', key: 'immigration', icon: Plane,     labelKey: 'immigration.title', prefixOnly: true },
  // Owner-only — non-owners can't reach the URL, so the slot
  // never appears for them in practice.
  { match: '/english',   key: 'english',    icon: Languages,    labelKey: 'english.title', prefixOnly: true },
  // Games — one entry per game so each path gets its own chip + label.
  // resolveEphemeralTab walks the list in order; the specific prefix
  // wins because there's no catch-all /games entry below them.
  { match: '/games/wordle',    key: 'gamesWordle',    icon: Gamepad2,       labelKey: 'games.wordle.tabLabel',    prefixOnly: true },
  { match: '/games/questions', key: 'gamesQuestions', icon: MessagesSquare, labelKey: 'games.questions.tabLabel', prefixOnly: true },
  { match: '/games/quiz',      key: 'gamesQuiz',      icon: Brain,          labelKey: 'games.quiz.tabLabel',      prefixOnly: true },
  { match: '/games/spy',       key: 'gamesSpy',       icon: VenetianMask,   labelKey: 'games.spy.tabLabel',       prefixOnly: true },
  // News is a fixed main tab now (replaced Photos), not ephemeral —
  // the bottom-nav active state for /news* is set directly in
  // App.jsx's HoistedBottomTabBar.
  { match: '/phases',    key: 'phases',     icon: Layers,       labelKey: 'phoenixTracker.phases.title' },
  { match: '/settings',  key: 'settings',   icon: SettingsIcon, labelKey: 'settings.title' },
  // /profile uses key='profileEdit' (NOT 'profile') so the
  // BottomTabBar's special-case avatar render keyed on 'profile'
  // doesn't fire here — the main profile slot still shows the photo,
  // the ephemeral chip stays as a UserCog so the two are visually
  // distinct on the bar at the same time.
  { match: '/profile',   key: 'profileEdit', icon: UserCog,     labelKey: 'profile.editPage.title' },
  { match: '/habits',    key: 'habits',     icon: CheckCircle2, labelKey: 'habits.title' },
  { match: '/physique',  key: 'physique',   icon: ScanLine,     labelKey: 'physique.title' },
  { match: '/sleep',     key: 'sleep',      icon: Moon,         labelKey: 'sleep.title' },
  { match: '/goal-pace', key: 'goalPace',   icon: Target,       labelKey: 'goalPace.title' },
  // Direct messages — list + detail. prefixOnly catches /messages/:id
  // when the user lands from a push notification.
  { match: '/messages',  key: 'messages',   icon: Inbox,        labelKey: 'messages.list.title', prefixOnly: true },
  // Friends graph — search, requests, friend list. Reached from
  // ProfileSheet or the friend-request push deep-link.
  { match: '/friends',   key: 'friends',    icon: Users2,       labelKey: 'friends.title' },
  // AI life search — natural-language retrieval across all modules.
  { match: '/search',    key: 'lifeSearch', icon: Radar,        labelKey: 'lifeSearch.title' },
  // People CRM — birthdays, contact cadence, gone-quiet nudges.
  { match: '/people',    key: 'people',     icon: Contact,      labelKey: 'people.title' },
  // Wishlist — want-to-buy items with finance-aware AI advice.
  { match: '/wishlist',  key: 'wishlist',   icon: Gift,         labelKey: 'wishlist.title' },
  // Moments — index + capture page. prefixOnly catches the
  // /moments/capture/:id deep-link from the daily push, lighting up
  // the same chip whether the user is browsing the index or shooting.
  { match: '/moments',   key: 'moments',    icon: Camera,       labelKey: 'moments.title', prefixOnly: true },
  // Reading shelf — index + new + book detail. prefixOnly so all
  // /reading/* paths surface the same chip.
  { match: '/reading',   key: 'reading',    icon: Library,      labelKey: 'reading.title', prefixOnly: true },
  { match: '/workout/new',       key: 'newSession', icon: Plus,         labelKey: 'workout.session.title' },
  { match: '/workout/history',   key: 'history',    icon: History,      labelKey: 'workout.history.title' },
  { match: '/workout/exercises', key: 'library',    icon: BookOpen,     labelKey: 'workout.exercises.title', prefixOnly: true },
  { match: '/workout/sessions',  key: 'session',    icon: Activity,     labelKey: 'workout.sessionDetail.title', prefixOnly: true },
  { match: '/workout/templates', key: 'templates',  icon: Layers,       labelKey: 'workout.templates.title' },
]

export function resolveEphemeralTab(pathname) {
  if (!pathname) return null
  for (const tab of EPHEMERAL_TABS) {
    if (tab.prefixOnly ? pathname.startsWith(tab.match) : pathname === tab.match) return tab
  }
  return null
}

// Phoenix sub-tabs that aren't in the fixed TABS bar but still want
// an ephemeral slot when active. Keyed by the activeTab string that
// /pages/phoenix/index dispatches on `phoenix:tab`. Different lookup
// mechanism from the routes above (no pathname involved) because
// these are in-page tab states, not URL paths.
export const EPHEMERAL_PHOENIX_TABS = {
  measurements: { key: 'measurements', icon: Ruler, labelKey: 'profile.measurements' },
  photos: { key: 'photos', icon: Camera, labelKey: 'phoenixTracker.tabs.photos' },
  // Progress moved off the primary bar when the Today dashboard
  // landed; ProfileSheet's tile dispatches state.tab='progress' and
  // this entry surfaces an ephemeral chip while we're on that pane.
  progress: { key: 'progress', icon: TrendingUp, labelKey: 'phoenixTracker.tabs.progress' },
}

export function resolveEphemeralPhoenixTab(phoenixTabKey) {
  return EPHEMERAL_PHOENIX_TABS[phoenixTabKey] || null
}
